import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useFirefliesStore } from '../stores/useFirefliesStore';
import { parseActionItems } from '../utils/transcript';

import type { FirefliesTranscript } from '../types';

import { useChecklistStore } from '@/apps/Checklist/stores/useChecklistStore';
import { useNotesStore } from '@/apps/Notes/stores/useNotesStore';
import { useToastStore } from '@/common/stores/useToastStore';
import { useWidgetStore } from '@/common/stores/useWidgetStore';

export const useFirefliesLogic = (apiKey: string, userEmail: string) => {
  const { t } = useTranslation();

  const widgets = useWidgetStore((s) => s.widgets);
  const notesWidgetId = useMemo(
    () => widgets.find((w) => w.widget_id === 'notes')?.id ?? null,
    [widgets],
  );
  const checklistWidgetId = useMemo(
    () => widgets.find((w) => w.widget_id === 'checklist')?.id ?? null,
    [widgets],
  );

  const {
    transcripts,
    selectedTranscript,
    transcriptDetailCache,
    lastFetchedAt,
    loading,
    error,
    clearError,
    fetchTranscripts,
    forceRefresh,
    fetchTranscriptDetail,
    setSelectedTranscript,
  } = useFirefliesStore();

  const addNote = useNotesStore((s) => s.addNote);
  const addItem = useChecklistStore((s) => s.addItem);
  const showToast = useToastStore((s) => s.showToast);

  useEffect(() => {
    if (apiKey) {
      fetchTranscripts(apiKey, userEmail || undefined);
    }
  }, [apiKey, userEmail, fetchTranscripts]);

  const detailTranscript = useMemo(() => {
    if (!selectedTranscript) return null;
    return transcriptDetailCache[selectedTranscript.id] ?? selectedTranscript;
  }, [selectedTranscript, transcriptDetailCache]);

  const handleRefresh = useCallback(() => {
    forceRefresh(apiKey, userEmail || undefined);
  }, [apiKey, userEmail, forceRefresh]);

  const handleOpenDetail = useCallback(
    async (transcript: FirefliesTranscript) => {
      setSelectedTranscript(transcript);
      if (apiKey && !transcript.sentences) {
        await fetchTranscriptDetail(apiKey, transcript.id);
      }
    },
    [apiKey, fetchTranscriptDetail, setSelectedTranscript],
  );

  const handleCloseDetail = useCallback(() => {
    setSelectedTranscript(null);
  }, [setSelectedTranscript]);

  const formatSummaryForNote = useCallback((transcript: FirefliesTranscript) => {
    const overview = (transcript.summary?.overview ?? transcript.summary?.short_summary ?? '')
      .replace(/\*\*/g, '');
    const parsed = parseActionItems(transcript.summary?.action_items);
    const parts = [overview];
    if (parsed.length > 0) {
      const lines = parsed.map((a) => {
        const prefix = a.person ? `${a.person}: ` : '';
        return `• ${prefix}${a.text}`;
      });
      parts.push('\n\nAction items:\n' + lines.join('\n'));
    }
    return parts.filter(Boolean).join('');
  }, []);

  const handleSaveToNotes = useCallback(
    async (transcript: FirefliesTranscript) => {
      if (!notesWidgetId) {
        showToast({ type: 'info', message: t('fireflies.addNotesWidgetFirst'), duration: 3 });
        return;
      }
      const content = formatSummaryForNote(transcript);
      const note = await addNote(notesWidgetId, transcript.title, content);
      if (note) {
        showToast({ type: 'success', message: 'Saved to Notes', duration: 2 });
      }
    },
    [notesWidgetId, addNote, formatSummaryForNote, showToast, t],
  );

  const handleAddToChecklist = useCallback(
    async (transcript: FirefliesTranscript) => {
      if (!checklistWidgetId) {
        showToast({ type: 'info', message: t('fireflies.addChecklistWidgetFirst'), duration: 3 });
        return;
      }
      const parsed = parseActionItems(transcript.summary?.action_items);
      for (const item of parsed) {
        const prefix = item.person ? `${item.person}: ` : '';
        await addItem(checklistWidgetId, `${prefix}${item.text}`);
      }
      showToast({
        type: 'success',
        message: `Added ${parsed.length} item${parsed.length > 1 ? 's' : ''} to Checklist`,
        duration: 2,
      });
    },
    [checklistWidgetId, addItem, showToast, t],
  );

  const handleAddActionToChecklist = useCallback(
    async (actionItem: string) => {
      if (!checklistWidgetId) {
        showToast({ type: 'info', message: t('fireflies.addChecklistWidgetFirst'), duration: 3 });
        return;
      }
      await addItem(checklistWidgetId, actionItem);
      showToast({ type: 'success', message: 'Added to Checklist', duration: 2 });
    },
    [checklistWidgetId, addItem, showToast, t],
  );

  return {
    transcripts,
    selectedTranscript,
    detailTranscript,
    lastFetchedAt,
    loading,
    error,
    hasNotesWidget: !!notesWidgetId,
    hasChecklistWidget: !!checklistWidgetId,
    clearError,
    handleRefresh,
    handleOpenDetail,
    handleCloseDetail,
    handleSaveToNotes,
    handleAddToChecklist,
    handleAddActionToChecklist,
  };
};
